import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { publicApi } from '@/lib/api/publicClient';
import { useT } from '@/lib/i18n/useT';

interface ClaimCodeResponse {
  redemptionCode: string;
  expiresAt: string | null;
  state: 'WON' | 'DELIVERED' | 'EXPIRED' | 'VOIDED';
}

/**
 * PRD Xotski v2.0 §11 Screen 5 — claim code reveal with the 5-min anti-
 * fraud countdown (PRD §22, Felix Pago principle).
 *
 * Flow:
 *   1. The bulk-submit returns the response UUID.
 *   2. The redemption row is created fire-and-forget on the backend.
 *   3. This component polls /xk-prize-redemption/by-response/:responseId
 *      until the row lands (max 6 tries × 500 ms), then renders the code
 *      and a live countdown.
 *
 * Color shift: green > 60 s · yellow 20–60 s · red < 20 s, matching the
 * PRD §11 traffic-light spec. When the timer hits zero the UI freezes at
 * 00:00 and asks the user to flag the BA so the operator can extend.
 */
export function ClaimCodeCountdown({ responseId }: { responseId: string }) {
  const t = useT();
  // PRD §11 — Sentinel set by SurveyWizard.dryRun. Skip the network poll
  // entirely and render a synthetic preview card so the manager sees the
  // shape of the screen without creating a real redemption row.
  const isPreview = responseId === 'preview-dryrun';

  const { data } = useQuery({
    queryKey: ['claim-code', responseId],
    queryFn: () =>
      publicApi.get<ClaimCodeResponse | null>(
        `/xk-prize-redemption/by-response/${responseId}`,
      ),
    // PRD §22 race-safe poll. createForWin runs fire-and-forget, so the
    // row may take a tick to land. Refetch every 500 ms for the first 3 s.
    refetchInterval: (q) => (q.state.data ? false : 500),
    refetchIntervalInBackground: false,
    enabled: !!responseId && !isPreview,
  });

  if (isPreview) {
    // 5-min synthetic expiry so the countdown animates. Code is mock-only.
    const fakeExpiry = new Date(Date.now() + 5 * 60_000).toISOString();
    return (
      <ClaimCodeCard code="PREVIEW1" expiresAt={fakeExpiry} />
    );
  }

  if (!data) {
    return (
      <p className="text-xs opacity-60">
        {t('publicSurvey.claim.preparing')}
      </p>
    );
  }

  return <ClaimCodeCard code={data.redemptionCode} expiresAt={data.expiresAt} />;
}

function ClaimCodeCard({
  code,
  expiresAt,
}: {
  code: string;
  expiresAt: string | null;
}) {
  const t = useT();
  const expiryMs = useMemo(
    () => (expiresAt ? new Date(expiresAt).getTime() : null),
    [expiresAt],
  );
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (expiryMs == null) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [expiryMs]);

  const remainingMs = expiryMs != null ? Math.max(0, expiryMs - now) : 0;
  const remainingSec = Math.floor(remainingMs / 1000);
  const mm = String(Math.floor(remainingSec / 60)).padStart(2, '0');
  const ss = String(remainingSec % 60).padStart(2, '0');

  const tone =
    remainingSec > 60
      ? 'var(--color-success)'
      : remainingSec > 20
        ? 'var(--color-warn)'
        : 'var(--color-error)';

  return (
    <div className="flex flex-col items-center gap-3">
      {/* PRD §11 Screen 5 + §12 — QR makes the operator's scanner happy
          without forcing the consumer to dictate the 8-char code. The
          payload is just the code (RedeemPrizePage hydrates `codeInput`
          from the decoded text). We render on white with quiet zone so
          the camera locks even on dark brand backgrounds. */}
      <div className="rounded-lg bg-white p-3" aria-hidden="true">
        <QRCodeSVG
          value={code}
          size={140}
          level="H"
          includeMargin={false}
        />
      </div>
      <div
        className="rounded-2xl border-2 px-8 py-4 font-mono text-4xl font-bold tracking-[0.3em] tabular-nums"
        style={{ borderColor: tone, color: tone }}
        aria-label={t('publicSurvey.claim.codeLabel')}
      >
        {code}
      </div>
      {expiryMs != null && (
        <div
          className="font-mono text-2xl font-bold tabular-nums"
          style={{ color: tone }}
        >
          {mm}:{ss}
        </div>
      )}
      <p className="max-w-xs text-center text-sm opacity-80">
        {remainingSec > 0
          ? t('publicSurvey.claim.showToBa')
          : t('publicSurvey.claim.expiredAskBa')}
      </p>
    </div>
  );
}
